import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6 text-sm">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: March 11, 2026</p>

          <div className="space-y-6 text-gray-700 text-sm leading-relaxed">
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">1. Introduction</h2>
              <p>
                Welcome to Brainathon Hub ("Memory Coach", "we", "us", or "our"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website at <strong>brainathonhub.com</strong> and use our AI-powered memory coaching platform.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">2. Information We Collect</h2>
              <p className="mb-2">We collect the following types of information:</p>
              <h3 className="font-medium text-gray-800 mt-3 mb-1">a) Personal Information</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Name, email address, and phone number (during registration)</li>
                <li>Password (stored in encrypted/hashed form)</li>
                <li>Payment information (processed securely via Instamojo; we do not store card details)</li>
              </ul>
              <h3 className="font-medium text-gray-800 mt-3 mb-1">b) Profile Information</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Age, location (state, city, area)</li>
                <li>Education details, exam preparation information</li>
                <li>Learning preferences and learner type</li>
                <li>Memory palace locations and interests</li>
                <li>Language preferences</li>
              </ul>
              <h3 className="font-medium text-gray-800 mt-3 mb-1">c) Usage Information</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Chat conversations with the AI memory coach</li>
                <li>Questions asked and techniques used</li>
                <li>Login activity and session data</li>
                <li>Daily usage patterns and question counts</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">3. How We Use Your Information</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>AI Personalization:</strong> Your profile data (age, exam, learner type, struggles, languages) is used to personalize AI responses and memory coaching techniques</li>
                <li><strong>Account Management:</strong> To create and manage your account, process payments, and provide customer support</li>
                <li><strong>Service Delivery:</strong> To provide AI-powered memory coaching, generate personalized memory hooks, and track your learning progress</li>
                <li><strong>Communication:</strong> To send welcome emails, payment confirmations, and important account notifications</li>
                <li><strong>Platform Improvement:</strong> To analyze usage patterns and improve our AI coaching quality and platform features</li>
                <li><strong>Trainer Management:</strong> If referred by a trainer, your basic progress data may be visible to your assigned trainer</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">4. Payment Information</h2>
              <p>
                All payments are processed securely through <strong>Instamojo</strong>, a trusted third-party payment gateway. We do not store your credit/debit card numbers or banking credentials on our servers. We only store transaction IDs and payment status for record-keeping purposes.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">5. Data Sharing</h2>
              <p className="mb-2">We do not sell your personal data. We may share information only in the following cases:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>With Trainers:</strong> If you are onboarded by or referred by a trainer, they can see your name, email, tier, question usage, and active status</li>
                <li><strong>AI Service Providers:</strong> Your chat messages are sent to AI providers (DeepSeek/Google Gemini) for generating responses. These providers process data as per their own privacy policies</li>
                <li><strong>Payment Processors:</strong> Instamojo receives your name, email, and phone for payment processing</li>
                <li><strong>Email Services:</strong> Your email is shared with our email service provider for sending transactional emails</li>
                <li><strong>Legal Requirements:</strong> We may disclose data if required by law or to protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">6. Data Security</h2>
              <p>
                We implement appropriate security measures to protect your personal information:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Passwords are hashed using bcrypt encryption</li>
                <li>All data transmission uses HTTPS/SSL encryption</li>
                <li>JWT tokens are used for secure authentication</li>
                <li>Database access is restricted and monitored</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">7. Data Retention</h2>
              <p>
                We retain your personal data for as long as your account is active or as needed to provide you services. Chat history is retained to provide continuity in your learning experience. You may request deletion of your account and associated data by contacting us.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">8. Your Rights</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Access:</strong> You can view your personal information in the My Profile section</li>
                <li><strong>Update:</strong> You can update your name, phone, and password from your profile</li>
                <li><strong>Delete:</strong> You can request account deletion by contacting our support</li>
                <li><strong>Data Export:</strong> You can request a copy of your personal data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">9. Cookies</h2>
              <p>
                We use local storage and session tokens to maintain your login session. We do not use third-party tracking cookies or advertising cookies.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">10. Children's Privacy</h2>
              <p>
                Our service is designed for students of all ages. For users under 18, we recommend parental consent before registration. We do not knowingly collect data from children under 13 without parental consent.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">11. Third-Party Services</h2>
              <p className="mb-2">Our platform integrates with the following third-party services:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Instamojo:</strong> Payment processing</li>
                <li><strong>DeepSeek / Google Gemini:</strong> AI response generation</li>
                <li><strong>Email Service:</strong> Transactional email delivery</li>
              </ul>
              <p className="mt-2">Each of these services has their own privacy policies governing their use of your data.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">13. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-2 bg-gray-50 rounded-lg p-4">
                <p><strong>Brainathon Hub</strong></p>
                <p>Email: support@brainathonhub.com</p>
                <p>Website: https://brainathonhub.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
